import { useCallback, useRef } from 'react';

/**
 * The part of a click event the hook needs. Satisfied by `React.MouseEvent` and by a native `MouseEvent`.
 */
export interface ICheckboxRangeSelectionEvent {
  shiftKey: boolean,
}

export interface IUseCheckboxRangeSelectionParams<T> {
  /** Items in the order they are rendered — a range is resolved against this order. */
  items: readonly T[],
  /** Current selected state of an item. */
  isSelected: (item: T) => boolean,
  /** Applies `isSelected` to the given items in a single update. */
  onChange: (items: T[], isSelected: boolean) => void,
  /** Items that cannot be toggled. They are ignored on click and skipped inside a range. */
  isDisabled?: (item: T) => boolean,
}

export type TGetCheckboxProps = (index: number) => {
  onClick: (event: ICheckboxRangeSelectionEvent) => void,
  onChange: () => void,
};

/** With no click yet, a shift-click reaches back to the top of the list. */
const IMPLICIT_ANCHOR_INDEX = 0;

/**
 * Gmail-like range selection for a list of checkboxes.
 */
const useCheckboxRangeSelection = <T, >(params: IUseCheckboxRangeSelectionParams<T>): TGetCheckboxProps => {
  const paramsRef = useRef(params);
  paramsRef.current = params;

  /** The last item clicked, by either kind of click — a shift-click moves it too. */
  const anchorIndexRef = useRef<number | null>(null);
  // Set on click and consumed by the change that the same click triggers, so a keyboard toggle —
  // which reports no modifier — can never inherit the previous mouse click's shift.
  const isShiftPressedRef = useRef(false);

  const handleClick = useCallback((event: ICheckboxRangeSelectionEvent): void => {
    isShiftPressedRef.current = event.shiftKey;
  }, []);

  const handleChange = useCallback((index: number): void => {
    const isShiftPressed = isShiftPressedRef.current;
    isShiftPressedRef.current = false;

    const { items, isSelected, onChange, isDisabled } = paramsRef.current;

    if (index < 0 || index >= items.length) {
      return;
    }

    const item = items[index];

    if (isDisabled?.(item)) {
      return;
    }

    const collect = (from: number, to: number): T[] => {
      const collected: T[] = [];

      for (let at = Math.max(from, 0); at <= Math.min(to, items.length - 1); at++) {
        if (!isDisabled?.(items[at])) {
          collected.push(items[at]);
        }
      }

      return collected;
    };

    const anchorIndex = anchorIndexRef.current ?? IMPLICIT_ANCHOR_INDEX;
    const isRange = isShiftPressed && anchorIndex < items.length;
    const nextIsSelected = !isSelected(item);

    anchorIndexRef.current = index;
    onChange(
      isRange ? collect(Math.min(anchorIndex, index), Math.max(anchorIndex, index)) : [item],
      nextIsSelected,
    );
  }, []);

  return useCallback((index: number) => ({
    onClick: handleClick,
    onChange: (): void => handleChange(index),
  }), [handleClick, handleChange]);
};

export default useCheckboxRangeSelection;
